import { Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CalendarHoliday, WorkCalendarSummary } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { DatePicker } from '../../../components/DatePicker';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { formatIsoDate, formatNumber } from '../../../lib/format';
import { isPendingHoliday } from '../api/calendars-api';
import { splitEarlier } from './calendars/calendar-year';
import type { HolidayActions } from './calendars/use-holiday-actions';
import styles from './HolidayPanel.module.css';

/**
 * The holiday list for one calendar, one year at a time — D14: "holidays are maintained annually at
 * ADMIN-10." Calendars A's right column, under the hours: the typed add row (date and name, kept beside
 * click-to-add on the year — critique), then the year's holidays, the ones already past folded into
 * "+N earlier". The year is chosen on the year view beside it.
 *
 * A holiday removes the whole day from every clock that runs on this calendar. Half-days are
 * deliberately not offered: the database carries a flag for them from S1, but no document says which
 * half is worked, and a guessed rule would silently skew every SLA number that crossed one.
 */

interface HolidayPanelProps {
  readonly calendar: WorkCalendarSummary;
  readonly year: number;
  /** The typed row may add to another year; the view follows it, so the add never seems to do nothing. */
  readonly onYearChange: (year: number) => void;
  readonly holidays: readonly CalendarHoliday[];
  readonly isLoading: boolean;
  readonly today: string;
  readonly canEdit: boolean;
  readonly actions: HolidayActions;
}

export function HolidayPanel({ calendar, year, onYearChange, holidays, isLoading, today, canEdit, actions }: HolidayPanelProps): ReactNode {
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [isEarlierOpen, setEarlierOpen] = useState(false);
  const [clashMessage, setClashMessage] = useState<string | null>(null);
  const { earlier, coming } = splitEarlier(holidays, year, today);
  const shown = isEarlierOpen ? holidays : coming;

  const submit = (): void => {
    const typedDate = date;
    const typedName = name.trim();
    if (typedDate === '' || typedName === '') return;
    const clash = holidays.find((holiday) => holiday.holidayDate === typedDate);
    if (clash) {
      // Said at once, from what is on screen; the server says the same for a year not shown.
      setClashMessage(`${calendar.name} already has a holiday on that date: ${clash.name}.`);
      return;
    }
    // Clears at once; a refusal puts back exactly what was typed (06-qol §4: nothing typed is lost).
    actions.add(typedDate, typedName, () => {
      setDate(typedDate);
      setName(typedName);
    });
    setDate('');
    setName('');
    const typedYear = Number(typedDate.slice(0, 4));
    if (typedYear !== year) onYearChange(typedYear);
  };

  return (
    <Card title="Holidays" meta={isLoading ? `${formatNumber(calendar.holidayCount)} in all` : `${formatNumber(holidays.length)} in ${year} · ${formatNumber(calendar.holidayCount)} in all`}>
      <div className={styles.panel}>
        {canEdit && (
          <form
            className={styles.add}
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <span className={styles.addFields}>
              <DatePicker
                label="Date"
                placeholder="dd-mm-yyyy"
                value={date}
                isInvalid={clashMessage !== null}
                timeZone={calendar.timezone}
                onChange={(value) => {
                  setDate(value);
                  setClashMessage(null);
                }}
              />
              <Input aria-label="What it is" placeholder="What it is" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
            </span>
            {clashMessage && (
              <p className={styles.error} role="alert">
                {clashMessage}
              </p>
            )}
            <span className={styles.addActions}>
              <span className={styles.hint}>Or click a date on the year</span>
              <Button type="submit" size="sm" variant="solid" icon={Plus} disabled={date === '' || name.trim() === ''}>
                Add
              </Button>
            </span>
          </form>
        )}

        {isLoading ? (
          <SkeletonLines count={3} />
        ) : holidays.length === 0 ? (
          <p className={styles.empty}>
            No holidays in {year} yet. {canEdit ? 'Click a date on the year, or type one above.' : ''} Every clock on this calendar stops for the whole day.
          </p>
        ) : (
          <>
            {shown.length === 0 && <p className={styles.empty}>Nothing still to come in {year}.</p>}
            <ul className={styles.list}>
              {shown.map((holiday) => (
                <li key={holiday.id} className={styles.row}>
                  <span className={styles.date}>{formatIsoDate(holiday.holidayDate)}</span>
                  <span className={styles.name} title={holiday.name}>
                    {holiday.name}
                  </span>
                  {canEdit && (
                    <Button size="sm" variant="ghost" icon={X} aria-label={`Remove ${holiday.name}`} disabled={isPendingHoliday(holiday)} onClick={() => actions.remove(holiday)} />
                  )}
                </li>
              ))}
            </ul>
            {earlier.length > 0 && (
              <button type="button" className={styles.more} aria-expanded={isEarlierOpen} onClick={() => setEarlierOpen(!isEarlierOpen)}>
                {isEarlierOpen ? `Hide the ${formatNumber(earlier.length)} earlier` : `+ ${formatNumber(earlier.length)} earlier in ${year}`}
              </button>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
